(ns ui.upload-manager
  (:require [simplemono.segment-upload.upload-manager :as upload-manager]
            [simplemono.segment-upload.util :as upload-util]))

(def stall-timeout-ms
  "An upload whose progress marker did not change for this long is
   given up. The upload manager retries segment and compose errors
   forever, and a stuck upload keeps `Uploading media...` and the
   disabled Share button in the editor forever (brain-90)."
  90000)

(def segment-restart-ms
  "An upload whose progress marker did not change for this long gets
   its in-flight segment requests restarted. The segment requests have
   no timeout and the upload manager only retries a segment with an
   `:error`, so a request whose answer never arrives keeps the upload
   stuck until `stall-timeout-ms` gives it up (STORRITO-V2-KR)."
  30000)

(defn ghost-upload?
  "An entry that `upload-manager/add-upload` did not create: it has no
   `:compose-drive!`. The upload manager removes a finished upload five
   seconds after its compose, but a late event of one of its requests
   writes through `upload-util/a-in`, whose `update-in` re-creates the
   entry as a skeleton. `drive!` then throws on that entry on every
   tick and no upload progresses anymore."
  [upload]
  (not (fn? (:compose-drive! upload))))

(defn drop-ghost-uploads
  [w]
  (update w
          :uploads
          (fn [uploads]
            (into {}
                  (remove (fn [[_id upload]]
                            (ghost-upload? upload)))
                  uploads))))

(defn upload-active?
  [upload]
  (not (or (:compose-done upload)
           (:aborted upload)
           (:callback-invoked upload))))

(defn progress-marker
  "Everything that changes while an upload advances through its
   segments and the compose. The progress of the segments is part of
   it, since the `:progress` of the upload never goes down: a segment
   that is sent again after a restart would otherwise not count as
   progress until it is done (STORRITO-V2-KR)."
  [upload]
  [(:progress upload)
   (mapv :progress (:segments upload))
   (count (filter :done (:segments upload)))
   (boolean (:segments-done upload))
   (boolean (:compose-request upload))])

(defn track-progress
  "Remembers for every active upload when its progress marker last
   changed."
  [w now]
  (update w
          :uploads
          (fn [uploads]
            (into {}
                  (map (fn [[id upload]]
                         [id
                          (if (upload-active? upload)
                            (let [marker (progress-marker upload)]
                              (if (= marker
                                     (:progress-marker upload))
                                upload
                                (assoc upload
                                       :progress-marker marker
                                       :progress-changed now)))
                            upload)]))
                  uploads))))

(defn stalled-upload-ids
  [w now]
  (keep (fn [[id upload]]
          (when (and (upload-active? upload)
                     (:progress-changed upload)
                     (<= stall-timeout-ms
                         (- now
                            (:progress-changed upload))))
            id))
        (:uploads w)))

(defn in-flight-segment?
  [segment]
  (and (:started segment)
       (not (:error segment))
       (not (:done segment))))

(defn segment-restart-due?
  [upload now]
  (boolean
    (and (upload-active? upload)
         (not (:segments-done upload))
         (:progress-changed upload)
         (some in-flight-segment?
               (:segments upload))
         (<= segment-restart-ms
             (- now
                (max (:progress-changed upload)
                     (or (:segments-restarted upload)
                         0)))))))

(defn in-flight-segment-indexes
  [upload]
  (into #{}
        (comp
          (filter in-flight-segment?)
          (map :index))
        (:segments upload)))

(defn mark-segments-stalled
  "An `:error` lets the upload manager retry the segment with a fresh
   upload URL request and PUT."
  [upload indexes now]
  (-> upload
      (assoc :segments-restarted now)
      (update :segment-restarts (fnil inc 0))
      (update :segments
              (fn [segments]
                (mapv (fn [segment]
                        (if (contains? indexes (:index segment))
                          (assoc segment
                                 :error :stalled
                                 :error-date (js/Date. now))
                          segment))
                      segments)))))

(defn restart-stalled-segments!
  "Aborts the PUT request of the in-flight segments of a stalled upload,
   so that a late answer does not interfere with the retry, and marks
   them as failed. The abort comes first: it synchronously sets the
   `:error :offline` of a request without answer (status 0), which
   would hide in the failure report that the segment stalled."
  [store]
  (let [now (.getTime (js/Date.))]
    (doseq [[id upload] (get-in @store
                                [:upload-manager :uploads])
            :when (segment-restart-due? upload now)]
      (let [indexes (in-flight-segment-indexes upload)]
        (doseq [segment (:segments upload)
                :when (contains? indexes (:index segment))
                :let [abort (:abort segment)]
                :when abort]
          (try
            (abort)
            (catch :default _)))
        (swap! store
               update-in
               [:upload-manager :uploads id]
               mark-segments-stalled
               indexes
               now)))))

(defn upload-failure
  "A plain summary of a stalled upload for its failure action and the
   error report: which segments failed with which server answer, and
   whether the compose failed."
  [upload now]
  {:id (:id upload)
   :blob-size (:blob-size upload)
   :progress (:progress upload)
   :segments-done (boolean (:segments-done upload))
   :stalled-ms (- now
                  (:progress-changed upload))
   :segment-errors (into []
                         (comp
                           (filter :error)
                           (map (fn [segment]
                                  {:index (:index segment)
                                   :error (:error segment)
                                   :status (get-in segment
                                                   [:error-response :status])
                                   :body (some-> (get-in segment
                                                         [:error-response :body])
                                                 (str)
                                                 (subs 0 200))})))
                         (:segments upload))
   :segments-in-flight (into []
                             (comp
                               (filter in-flight-segment?)
                               (map :index))
                             (:segments upload))
   :segment-restarts (or (:segment-restarts upload)
                         0)
   :compose-error (some-> (:compose-error upload)
                          (str))})

(defn report-failure!
  [failure]
  (let [error (js/Error. (str "Upload stalled: "
                              (pr-str failure)))]
    (js/console.error error)
    (when (exists? js/Sentry)
      (js/Sentry.captureException error))))

(def upload-failed-alert-text
  "A reload would discard the unsaved design. The media stays in the
   design instead, and saving starts its upload again (see
   `ui.editor2.actions/save!`)."
  "Uploading the media failed. Please check your internet connection, then click Save to try the upload again. Contact support if this issue persists.")

(def media-unreadable-alert-text
  "The media could not be read for the upload. Please remove it from your design and add it again.")

(defn fail-upload!
  "Aborts the stalled upload, removes it from the upload manager and
   dispatches its `:failure-action`, or an alert when it has none."
  [store id now]
  (let [state @store
        upload (get-in state
                       [:upload-manager :uploads id])
        failure (upload-failure upload now)
        dispatch! (:replicant/dispatch! state)]
    (when-let [abort (:abort upload)]
      (try
        (abort)
        (catch :default _)))
    (swap! store
           update-in
           [:upload-manager :uploads]
           dissoc
           id)
    (report-failure! failure)
    (dispatch! {:actions [(if-let [action (:failure-action upload)]
                            (conj action failure)
                            [:js/alert upload-failed-alert-text])]})))

(defn fail-stalled-uploads!
  [store]
  (let [now (.getTime (js/Date.))
        state (swap! store
                     update
                     :upload-manager
                     track-progress
                     now)]
    (doseq [id (stalled-upload-ids (:upload-manager state)
                                   now)]
      (fail-upload! store
                    id
                    now))))

(defn- safe-drive!
  "Wraps upload-manager/drive! in a try-catch. The drive! function can throw
   when an upload is cleaned up concurrently between reading the upload map
   and invoking :compose-drive! on an upload entry that no longer exists."
  [a]
  (try
    (upload-manager/drive! a)
    (catch :default _)))

(defn tick!
  [store]
  (swap! store
         update
         :upload-manager
         drop-ghost-uploads)
  (fail-stalled-uploads! store)
  (restart-stalled-segments! store)
  (safe-drive!
    (upload-util/a-in
      (upload-util/atom->a store)
      [:upload-manager])))

(defn add-driver!
  [w]
  (let [store (:ui/store w)]
    (js/setInterval (fn []
                      (tick! store))
                    500))
  w)

(defn report-unreadable!
  "The `:blob-uri` of an upload could not be fetched, so there is
   nothing to upload: dispatches the `:failure-action` of the upload, or
   an alert when it has none. Without it the asset would stay without
   an upload and without a word to the user."
  [store params error]
  (js/console.error error)
  (let [dispatch! (:replicant/dispatch! @store)]
    (dispatch! {:actions [(if-let [action (:failure-action params)]
                            (conj action {:id (:id params)
                                          :unreadable (str error)})
                            [:js/alert media-unreadable-alert-text])]})))

(defn add-upload!
  [{:keys [store action]}]
  (let [params (second action)]
    (-> (js/fetch (:blob-uri params))
        (.then (fn [response]
                 (.blob response)))
        (.then (fn [blob]
                 (swap! store
                        (fn [state]
                          (update
                            state
                            :upload-manager
                            (fn [w]
                              ;; A finished or aborted upload stays for
                              ;; five seconds until the upload manager
                              ;; cleans it up. It must not swallow a new
                              ;; upload of the same media (see
                              ;; `ui.editor2.actions/save!`).
                              (if (some-> (get-in w
                                                  [:uploads
                                                   (:id params)])
                                          (upload-active?))
                                w
                                (upload-manager/add-upload
                                  w
                                  {:id (or (:id params)
                                           (.getTime (js/Date.)))
                                   :failure-action (:failure-action params)
                                   :callback (fn [upload-result]
                                               ;; I don't know why it needs to be
                                               ;; wrapped in a
                                               ;; `requestAnimationFrame` but
                                               ;; otherwise the action has no
                                               ;; effect:
                                               (js/requestAnimationFrame
                                                 (fn []
                                                   (let [dispatch! (:replicant/dispatch! state)]
                                                     (dispatch!
                                                       {:actions
                                                        [(conj (:callback-action params)
                                                               upload-result)]})))))
                                   :blob blob})))))))
               (fn [error]
                 (report-unreadable! store params error))))))
